import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal, Scroller } from '@/shared/ui/Modal'
import { Empty } from '@/shared/ui/Panel'
import { Segmented } from '@/shared/ui/Segmented'
import { Select } from '@/shared/ui/Select'
import { Strip } from '@/shared/ui/Strip'
import { Switch } from '@/shared/ui/Switch'
import { EditIcon, TrashIcon } from '@/shared/ui/icons'
import type { AutoWithdrawalResponse, Currency } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { crypto } from '@/shared/lib/format'
import { useCabinet, useStores, useUi } from '@/stores/RootStore'
import { DialogPage } from './DialogPage'
import styles from './Auto.module.css'
import { explain } from '@/shared/lib/errors'

const TYPES = [
  { value: 'by_amount' as const, label: 'По сумме' },
  { value: 'by_time' as const, label: 'По времени' },
]

/** Человеческое описание правила — оно же заголовок диалога правки. */
export function ruleTitle(rule: AutoWithdrawalResponse): string {
  const ticker = coinOf(rule.currency).ticker
  if (rule.type === 'by_amount') {
    return `При балансе от ${crypto(rule.threshold)} ${ticker}`
  }
  return `Ежедневно в ${(rule.trigger_time ?? '').slice(0, 5) || '00:00'}`
}

/**
 * Автовыводы.
 *
 * Единственный экран, где заданное здесь однажды сработает ночью
 * и без человека. Всё остальное на этой странице подчинено тому,
 * чтобы правило нельзя было сохранить «почти правильным».
 */
export const AutoWithdrawalsPage = observer(function AutoWithdrawalsPage() {
  const { rules: store } = useStores()
  const cabinet = useCabinet()
  const ui = useUi()

  useEffect(() => {
    void cabinet.rules.load()
    void cabinet.wallets.load()
  }, [cabinet])

  const noWallets = cabinet.wallets.loaded && store.wallets.length === 0

  return (
    <>
    <DialogPage
      tall
      hidden={store.dialog.kind !== 'none'}
      title="Автовыводы"
      note="Настройка вывода средств по времени или по сумме на балансе."
      /* Без иконки: в макете у кнопки диалога только подпись. */
      actions={
        <Button variant="primary" disabled={noWallets} onClick={() => store.openAdd()}>
          Добавить
        </Button>
      }
    >
      <>
        {cabinet.rules.error && !cabinet.rules.loaded ? (
          <Strip tone="bad"
                 aside={<Button variant="secondary" onClick={() => void cabinet.rules.load()}>Ещё раз</Button>}>
            {explain(cabinet.rules.error)}
          </Strip>
        ) : null}

        {noWallets ? (
          <Empty title="Сначала добавьте кошелёк"
                 action={
                   <button type="button" className={styles.link}
                           onClick={() => ui.openDialog({ kind: 'wallets' })}>
                     Перейти к кошелькам
                   </button>
                 } />
        ) : store.items.length === 0 && cabinet.rules.loaded ? null : (
          <Scroller>
            <ul className={styles.list}>
              {store.items.map((rule) => <RuleRow key={rule.id} rule={rule} />)}
            </ul>
          </Scroller>
        )}

      </>
    </DialogPage>

    <RuleDialogs />
    </>
  )
})

const RuleRow = observer(function RuleRow({ rule }: { rule: AutoWithdrawalResponse }) {
  const { rules: store } = useStores()
  const coin = coinOf(rule.currency)
  return (
    <li className={styles.item}>
      <span className={styles.body}>
        <span className={styles.text}>
          <span className={styles.kind}>
            {rule.type === 'by_amount' ? 'Вывод по сумме' : 'Вывод по времени'}
          </span>
          <span className={styles.cond}>{ruleTitle(rule)}</span>
        </span>
        {/*
          Состояние подписью с точкой — как в макете. Переключатель
          рядом остаётся, но без собственной подписи: две надписи об
          одном и том же состоянии в одной строке читаются как две
          разные настройки.

          Монета и адрес стоят В ТОЙ ЖЕ строке, а не отдельной третьей:
          в макете строка правила ровно 112px, а лишняя строка делала
          её 138. Убрать их совсем нельзя — два правила на одну монету
          с разными кошельками выглядели бы в списке одинаково, и
          выключить можно не то.
        */}
        <span className={styles.footer}>
          <span className={rule.enabled
            ? `${styles.state} ${styles.on}`
            : `${styles.state} ${styles.off}`}>
            <span className={styles.dot} />
            <span>{rule.enabled ? 'Активен' : 'Отключён'}</span>
          </span>
          <span className={styles.meta}>
            · {coin.ticker} ({networkOf(rule.currency)}) · {store.walletName(rule.wallet_id)}
          </span>
        </span>
        {/* Бекенд гасит правило сам, когда оно раз за разом не проходит.
            Молчаливо выключенный автовывод — это деньги, которые не
            пришли, и человек узнаёт об этом последним. */}
        {rule.disabled_reason ? (
          <span className={styles.why}>Отключён: {rule.disabled_reason}</span>
        ) : null}
      </span>

      <span className={styles.acts}>
        <Switch checked={rule.enabled} disabled={store.busy}
                onChange={(next) => void store.toggle(rule, next)} />
        <Button variant="icon" aria-label={`Изменить: ${ruleTitle(rule)}`}
                onClick={() => store.openEdit(rule)}>
          <EditIcon />
        </Button>
        <Button variant="icon" tone="bad" aria-label={`Удалить: ${ruleTitle(rule)}`}
                onClick={() => store.openRemove(rule)}>
          <TrashIcon />
        </Button>
      </span>
    </li>
  )
})

const RuleDialogs = observer(function RuleDialogs() {
  const { rules: store } = useStores()
  const dialog = store.dialog
  if (dialog.kind === 'none') return null

  if (dialog.kind === 'remove') {
    return (
      /* Вопрос — подзаголовок окна (note), а не тело: в макете
         (Delete, карточка 212) он стоит под заголовком с зазором 8.
         Отдельным телом он добавляет ещё 32 зазора и делает окно
         на 48px выше макета. */
      <Modal title="Удалить автовывод?"
             note={`Вы уверены, что хотите удалить автовывод «${ruleTitle(dialog.rule)}»?`}
             onClose={() => store.close()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.close()}>Не удалять</Button>
                 <Button variant="primary" busy={store.busy}
                         onClick={() => void store.remove()}>Да, удалить</Button>
               </>
             } />
    )
  }

  const adding = dialog.kind === 'add'
  const draft = store.draft
  const currencyOptions = store.availableCurrencies.map((currency: Currency) => ({
    value: currency,
    label: `${coinOf(currency).ticker} (${networkOf(currency)})`,
    sub: coinOf(currency).label,
    dot: coinOf(currency).accent,
  }))
  const walletOptions = store.walletsForDraft.map((wallet) => ({
    value: wallet.id, label: wallet.name, sub: wallet.address,
  }))

  return (
    <Modal title={adding ? 'Добавить автовывод' : ruleTitle(dialog.rule)}
           onClose={() => store.close()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.close()}>Отмена</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.submit()}>
                 {adding ? 'Добавить' : 'Сохранить'}
               </Button>
             </>
           }>
      {/* Поля диалога — своей колонкой с зазором 16, а не детьми окна
          с зазором 32: так во всех кадрах макета (Frame 339208/339250,
          зазор 16). Разница накапливается по числу полей и делает
          карточку на 66px выше — на глаз это «окно чуть больше»,
          а по макету это другое окно. */}
      <div className={styles.fields}>
        {adding ? (
          <div className={styles.typeRow}>
            <span className={styles.typeLabel}>Тип автовывода</span>
            <Segmented wide ariaLabel="Тип автовывода" options={TYPES} value={draft.type}
                       onChange={(type) => store.patch({ type })} />
          </div>
        ) : null}

        <Select label="Валюта" placeholder="Выберите валюту" options={currencyOptions}
                value={draft.currency} disabled={!adding}
                onChange={(value) => store.patch({ currency: value as Currency })}
                error={store.errors.currency ?? null} />

        {draft.type === 'by_amount' ? (
          <Field label="Сумма активации" placeholder="Укажите сумму" numeric inputMode="decimal"
                 value={draft.threshold}
                 param={draft.currency ? coinOf(draft.currency).ticker : undefined}
                 onChange={(e) => store.patch({ threshold: e.target.value })}
                 error={store.errors.threshold ?? null} />
        ) : (
          /* Время ВСЕГДА в UTC, и это написано прямо в поле. Выбора
             пояса в макете нет, а «в 12:00» без указания часов — это
             вывод в чужое время суток: мерчант ждёт полдень, деньги
             уходят ночью. Пояс в параметре поля стоит ноль пикселей
             и отвечает на вопрос до того, как он возникнет. */
          <Field label="Время активации" placeholder="12:00" type="text" inputMode="numeric"
                 value={draft.triggerTime} param="UTC"
                 onChange={(e) => store.patch({ triggerTime: e.target.value })}
                 error={store.errors.triggerTime ?? null} />
        )}

        <Select label="Кошелёк"
                /* Подпись одна и та же независимо от того, выбрана ли
                   валюта: так в макете, и так короче — «сначала выберите
                   валюту» человек и без подсказки видит по выключенному
                   полю. */
                placeholder="Выберите кошелёк"
                options={walletOptions} value={draft.walletId}
                disabled={draft.currency === null}
                onChange={(value) => store.patch({ walletId: value })}
                error={store.errors.walletId ?? null} />

        {/* Не подсказка, а предупреждение: список выше уже отфильтрован
            по валюте, и человек должен понимать, почему он короткий. */}
        {draft.currency !== null && walletOptions.length === 0 ? (
          <Strip tone="warn">
            Нет кошелька для {coinOf(draft.currency).ticker} ({networkOf(draft.currency)})
          </Strip>
        ) : null}

        {/* Блок «Действия» — как в макете (Auto-Withdrawals 4:44343):
            подпись, под ней переключатель в рамке, а при правке ещё
            и удаление. Кнопка удаления живёт здесь, а не рядом
            с «Сохранить»: удалить правило вывода мимоходом нельзя,
            и подтверждение всё равно спросят отдельным окном. */}
        <div className={styles.typeRow}>
          <span className={styles.typeLabel}>Действия</span>

          <Switch boxed checked={draft.enabled} label="Включить автовывод"
                  onChange={(enabled) => store.patch({ enabled })} />

          {adding ? null : (
            <span className={styles.removeRow}>
              <Button variant="secondary" block onClick={() => store.openRemove(dialog.rule)}>
                Удалить
              </Button>
            </span>
          )}
        </div>
      </div>
    </Modal>
  )
})
